import type { ElementType, ReactNode } from 'react';
import {
  Box,
  Card,
  CardBody,
  HStack,
  Icon,
  SimpleGrid,
  Skeleton,
  Stack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiLayers, FiPocket, FiShield } from 'react-icons/fi';
import EmptyState from './EmptyState';

export interface BalancesProps {
  isLoading?: boolean;
  isEmpty?: boolean;
  helper?: ReactNode;
  total: number;
  withdrawable: number;
  locked: number;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value / 100);

const Balances = ({
  isLoading = false,
  isEmpty = false,
  total,
  withdrawable,
  locked,
}: BalancesProps) => {
  const iconBg = useColorModeValue('brand.50', 'gray.700');
  const iconColor = useColorModeValue('brand.500', 'brand.300');

  if (isEmpty) {
    return (
      <EmptyState
        title="Sem informações de saldo"
        description="As informações de saldo do jogador serão exibidas aqui assim que houver movimentações registradas."
      />
    );
  }

  const cards = [
    {
      label: 'Saldo total',
      value: total,
      helper: `Saldo total será: ${formatCurrency(locked)} (indisponível para saque)`,
      icon: FiLayers as ElementType,
    },
    {
      label: 'Saldo disponível para saque',
      value: withdrawable,
      helper: 'Valor disponível imediatamente para solicitações de saque.',
      icon: FiPocket as ElementType,
    },
    {
      label: 'Saldo indisponível para saque',
      value: locked,
      helper: 'Valor reservado para uso interno e liberações futuras.',
      icon: FiShield as ElementType,
    },
  ];

  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 4, md: 6 }}>
      {cards.map(({ label, value, helper, icon }) => (
        <Skeleton key={label} isLoaded={!isLoading} rounded="lg">
          <Card variant="elevated" h="100%">
            <CardBody>
              <Stack spacing={6}>
                <HStack spacing={4} align="center">
                  <Box
                    bg={iconBg}
                    color={iconColor}
                    rounded="full"
                    p={3}
                    display="inline-flex"
                  >
                    <Icon as={icon} boxSize={5} />
                  </Box>
                  <Stat>
                    <StatLabel color="text.600" fontWeight="medium">
                      {label}
                    </StatLabel>
                    <StatNumber fontSize="2xl" color="text.900">
                      {formatCurrency(value)}
                    </StatNumber>
                    <StatHelpText color="text.600">{helper}</StatHelpText>
                  </Stat>
                </HStack>
              </Stack>
            </CardBody>
          </Card>
        </Skeleton>
      ))}
    </SimpleGrid>
  );
};

export default Balances;
